import React from "react";
import * as MUI from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { CRIMSON_RED } from "../../../../../../../../enums/Colors";
import ConfirmAndCancelButtons from "../../../../../../../../components/buttons/ConfirmAndCancelButtons";
import { useFormDeleteNoConfirm } from "../../../../../../../../hooks/useFormDelete";
import { PRICE_DELETE_ID } from "../../../../../../../../enums/ApiPaths";
import { SERVICES } from "../../../../../../../../enums/QueryKeys";
import { useQueryClient } from "@tanstack/react-query";
import logger from "../../../../../../../../utils/logger";

export default function DeleteServiceDialog({ service, clinicId, onCancel, onDeleted }) {
  const { t } = useTranslation();
  const deletion = useFormDeleteNoConfirm(PRICE_DELETE_ID, service?.id, null, 0);
  const queryClient = useQueryClient();

  async function onConfirm() {
    try {
      await deletion.submitForm();
      queryClient.invalidateQueries({ queryKey: [SERVICES + clinicId] });
      onCancel();
      onDeleted?.(service);
    } catch (err) {
      logger.error("Failed to delete service", err);
    }
  }

  return (
    <MUI.Dialog
      open={Boolean(service)}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
      data-testid="warehouse.services.delete-dialog"
    >
      <MUI.Stack direction="row" justifyContent="flex-end" sx={{ p: 1 }}>
        <MUI.IconButton
          onClick={onCancel}
          size="small"
          data-testid="warehouse.services.delete-dialog.close-button"
        >
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Stack>
      <MUI.Stack pt={0} justifyContent="center" alignItems="center">
        <MUI.Avatar
          sx={{ bgcolor: MUI.alpha(CRIMSON_RED, 0.12), width: 64, height: 64 }}
        >
          <WarningAmberOutlinedIcon
            htmlColor={CRIMSON_RED}
            sx={{ fontSize: 34 }}
          />
        </MUI.Avatar>
      </MUI.Stack>
      <MUI.DialogTitle
        sx={{ textAlign: "center", fontWeight: 700 }}
        data-testid="warehouse.services.delete-dialog.title"
      >
        {t("Delete")} {service?.title}
      </MUI.DialogTitle>
      <MUI.DialogContent sx={{ textAlign: "center" }}>
        <MUI.Typography color="text.secondary" data-testid="warehouse.services.delete-dialog.message">
          {t("DeleteServiceConfirmation")}
        </MUI.Typography>
      </MUI.DialogContent>
      <MUI.DialogActions sx={{ p: 2, pt: 0 }}>
        <ConfirmAndCancelButtons
          confirmTitle="Delete"
          cancelTitle=""
          onConfirm={onConfirm}
          isConfirmLoading={deletion.loading}
          confirmButtonProps={{ color: "error" }}
          testId="warehouse.services.delete-dialog"
        />
      </MUI.DialogActions>
    </MUI.Dialog>
  );
}
